<template>
  <v-container fluid class="ai-admin">
    <div class="d-flex align-center mb-4">
      <div>
        <h1 class="text-h5 font-weight-medium">{{ t('aiAdmin.title') }}</h1>
        <div class="text-body-2 text-medium-emphasis">{{ t('aiAdmin.description') }}</div>
      </div>
    </div>

    <v-tabs v-model="tab" color="primary" class="mb-4">
      <v-tab value="usage" prepend-icon="mdi-chart-timeline-variant">{{ t('aiAdmin.tabs.usage') }}</v-tab>
      <v-tab value="runs" prepend-icon="mdi-notebook-outline">{{ t('aiAdmin.tabs.runs') }}</v-tab>
      <v-tab v-if="isSuperAdmin" value="config" prepend-icon="mdi-robot-outline">{{ t('aiAdmin.tabs.config') }}</v-tab>
    </v-tabs>

    <v-window v-model="tab">
      <!-- Consumi e credito: admin e superadmin (il saldo reale DeepSeek solo superadmin) -->
      <v-window-item value="usage">
        <!-- Consumo IA registrato dal sistema (tabella AiUsage) -->
        <v-card outlined>
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-chart-timeline-variant</v-icon>
            {{ t('admConfig.usage.title') }}
          </v-card-title>
          <v-card-text>
            <div class="d-flex align-center flex-wrap ga-3 mb-2">
              <v-text-field
                v-model="usageFrom" type="date" density="compact" hide-details
                :label="t('admConfig.usage.from')" style="max-width: 190px"
              />
              <v-text-field
                v-model="usageTo" type="date" density="compact" hide-details
                :label="t('admConfig.usage.to')" style="max-width: 190px"
              />
              <v-btn
                color="primary" variant="tonal" :loading="usageLoading"
                prepend-icon="mdi-refresh" @click="loadUsage"
              >
                {{ t('admConfig.usage.refresh') }}
              </v-btn>
            </div>

            <div class="text-caption text-medium-emphasis mb-3">
              {{ t('admConfig.usage.hint') }}
            </div>

            <v-alert v-if="usageError" type="error" variant="tonal" density="comfortable" class="mb-3">
              {{ usageError }}
            </v-alert>

            <template v-if="usage">
              <div class="d-flex align-center flex-wrap mb-4">
                <div class="mr-8">
                  <div class="text-caption text-medium-emphasis">{{ t('admConfig.usage.totalCost') }}</div>
                  <div class="text-h4">{{ formatUsdShort(usage.totals.costUsd) }}</div>
                </div>
                <!-- Costo reale, margine e fee: arrivano solo al superadmin (feePercent != null) -->
                <template v-if="usage.feePercent != null">
                  <div class="mr-8">
                    <div class="text-caption text-medium-emphasis">{{ t('admConfig.usage.providerCost') }}</div>
                    <div class="text-h6">{{ formatUsdShort(usage.totals.providerCostUsd) }}</div>
                  </div>
                  <div class="mr-8">
                    <div class="text-caption text-medium-emphasis">{{ t('admConfig.usage.margin') }}</div>
                    <div class="text-h6 text-success">{{ formatUsdShort(usage.totals.costUsd - usage.totals.providerCostUsd) }}</div>
                  </div>
                  <div class="mr-8">
                    <div class="text-caption text-medium-emphasis">{{ t('admConfig.usage.feePercent') }}</div>
                    <div class="text-h6">{{ usage.feePercent }}%</div>
                  </div>
                </template>
                <div class="mr-8">
                  <div class="text-caption text-medium-emphasis">{{ t('admConfig.usage.totalRuns') }}</div>
                  <div class="text-h6">{{ usage.totals.runs }}</div>
                </div>
                <div>
                  <div class="text-caption text-medium-emphasis">{{ t('projects.cost.failures') }}</div>
                  <div class="text-h6">{{ usage.totals.failedRuns }}</div>
                </div>
              </div>

              <v-alert
                v-if="usage.totals.legacyRuns" type="info" variant="tonal" density="compact" class="mb-3"
              >
                {{ t('projects.cost.legacyHint', { n: usage.totals.legacyRuns }) }}
              </v-alert>
              <v-alert
                v-if="usage.totals.unpricedRuns" type="warning" variant="tonal" density="compact" class="mb-3"
              >
                {{ t('projects.cost.unpricedHint', { n: usage.totals.unpricedRuns }) }}
              </v-alert>
              <v-alert
                v-if="usage.totals.partialRuns" type="warning" variant="tonal" density="compact" class="mb-3"
              >
                {{ t('projects.cost.partialHint', { n: usage.totals.partialRuns }) }}
              </v-alert>

              <div class="text-subtitle-2 mb-1">{{ t('admConfig.usage.byModel') }}</div>
              <v-data-table
                :headers="usageModelHeaders" :items="usage.byEngineModel"
                density="compact" :items-per-page="10" class="mb-5 elevation-1"
              />

              <div class="text-subtitle-2 mb-1">{{ t('admConfig.usage.topProjects') }}</div>
              <v-data-table
                :headers="usageProjectHeaders" :items="usage.topProjects"
                density="compact" :items-per-page="10" class="elevation-1"
              />
            </template>
          </v-card-text>
        </v-card>

        <!-- Credito virtuale DeepSeek del cliente: ricarica configurata meno l'addebitato (fee
             inclusa). Tutti gli admin. Il saldo reale del conto DeepSeek sta nella card sotto,
             solo superadmin. -->
        <v-card outlined>
          <v-card-title>
            <v-icon left>mdi-wallet-outline</v-icon>
            {{ t('admConfig.deepSeekCredit.title') }}
          </v-card-title>
          <v-card-text>
            <div class="d-flex align-center flex-wrap" style="gap: 12px;">
              <v-btn
                variant="tonal"
                color="primary"
                :loading="creditLoading"
                prepend-icon="mdi-refresh"
                @click="loadDeepSeekCredit"
              >
                {{ t('admConfig.deepSeekBalance.check') }}
              </v-btn>
              <span v-if="credit?.asOf" class="text-caption text-medium-emphasis">
                {{ t('admConfig.billing.asOf', { time: formatDateTime(credit.asOf) }) }}
              </span>
            </div>
            <div class="text-caption text-medium-emphasis mt-2">
              {{ t('admConfig.deepSeekCredit.hint') }}
            </div>

            <div v-if="credit" class="mt-4">
              <template v-if="credit.configured">
                <div class="text-caption text-medium-emphasis">{{ t('admConfig.deepSeekCredit.remaining') }}</div>
                <div class="text-h4" :class="Number(credit.remainingUsd) <= 0 ? 'text-error' : 'text-success'">
                  {{ formatUsdShort(credit.remainingUsd) }}
                </div>
                <div class="text-body-2 mt-1">
                  {{ t('admConfig.deepSeekCredit.spentSince', { amount: formatUsdShort(credit.spentUsd), date: credit.sinceDate }) }}
                  · {{ t('admConfig.deepSeekCredit.topup', { amount: formatUsdShort(credit.topupUsd) }) }}
                  · {{ t('admConfig.deepSeekCredit.runs', { n: credit.runs }) }}
                </div>
              </template>
              <template v-else>
                <div class="text-caption text-medium-emphasis">{{ t('admConfig.deepSeekCredit.spentLabel') }}</div>
                <div class="text-h5">{{ formatUsdShort(credit.spentUsd) }}</div>
                <div class="text-body-2 mt-1">
                  {{ t('admConfig.billing.spentSinceShort', { date: credit.sinceDate }) }}
                  · {{ t('admConfig.deepSeekCredit.runs', { n: credit.runs }) }}
                </div>
                <v-alert type="info" variant="text" density="compact" class="mt-2 px-0">
                  {{ credit.message || t('admConfig.deepSeekCredit.notConfigured') }}
                </v-alert>
              </template>
            </div>

            <v-alert v-if="creditError" type="error" variant="tonal" density="comfortable" class="mt-4">
              {{ creditError }}
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- Saldo REALE del conto DeepSeek: e' il conto del fornitore del servizio, solo superadmin. -->
        <v-card v-if="isSuperAdmin" outlined>
          <v-card-title>
            <v-icon left>mdi-bank-outline</v-icon>
            {{ t('admConfig.deepSeekBalance.title') }}
          </v-card-title>
          <v-card-text>
            <div class="d-flex align-center flex-wrap" style="gap: 12px;">
              <v-btn
                variant="tonal"
                color="primary"
                :loading="deepSeekLoading"
                prepend-icon="mdi-refresh"
                @click="loadDeepSeekBalance"
              >
                {{ t('admConfig.deepSeekBalance.check') }}
              </v-btn>
              <v-btn
                variant="text"
                color="primary"
                prepend-icon="mdi-open-in-new"
                href="https://platform.deepseek.com/top_up"
                target="_blank"
                rel="noopener"
              >
                {{ t('admConfig.deepSeekBalance.openPlatform') }}
              </v-btn>
              <span v-if="deepSeek?.asOf" class="text-caption text-medium-emphasis">
                {{ t('admConfig.billing.asOf', { time: formatDateTime(deepSeek.asOf) }) }}
              </span>
            </div>

            <v-alert v-if="deepSeek && !deepSeek.available" :type="deepSeek.keyConfigured ? 'warning' : 'info'" variant="tonal" density="comfortable" class="mt-4">
              {{ deepSeek.message || t('admConfig.deepSeekBalance.error') }}
            </v-alert>

            <div v-else-if="deepSeek" class="mt-4">
              <v-alert v-if="deepSeek.canUse === false" type="error" variant="tonal" density="comfortable" class="mb-3">
                {{ t('admConfig.deepSeekBalance.unusable') }}
              </v-alert>
              <div v-if="!deepSeek.balances?.length" class="text-body-2">{{ t('admConfig.deepSeekBalance.empty') }}</div>
              <div v-for="line in deepSeek.balances" :key="line.currency" class="d-flex align-end flex-wrap mb-2" style="gap: 32px;">
                <div>
                  <div class="text-caption text-medium-emphasis">{{ t('admConfig.deepSeekBalance.total') }}</div>
                  <div class="text-h4" :class="deepSeek.canUse === false || Number(line.totalBalance) <= 0 ? 'text-error' : 'text-success'">
                    {{ formatMoney(line.totalBalance, line.currency) }}
                  </div>
                </div>
                <div>
                  <div class="text-caption text-medium-emphasis">{{ t('admConfig.deepSeekBalance.toppedUp') }}</div>
                  <div class="text-h6">{{ formatMoney(line.toppedUpBalance, line.currency) }}</div>
                </div>
                <div>
                  <div class="text-caption text-medium-emphasis">{{ t('admConfig.deepSeekBalance.granted') }}</div>
                  <div class="text-h6">{{ formatMoney(line.grantedBalance, line.currency) }}</div>
                </div>
              </div>
            </div>

            <v-alert v-if="deepSeekError" type="error" variant="tonal" density="comfortable" class="mt-4">
              {{ deepSeekError }}
            </v-alert>
          </v-card-text>
        </v-card>
      </v-window-item>

      <!-- Esecuzioni IA di tutti i processi: admin e superadmin -->
      <v-window-item value="runs">
        <AiRunsLog />
      </v-window-item>

      <!-- Configurazione IA — solo superadmin -->
      <v-window-item v-if="isSuperAdmin" value="config">
        <!-- Impostazioni generali delle verifiche (Classes/aiSettings.json) -->
        <v-card outlined>
          <v-card-title>
            <v-icon left>mdi-brain</v-icon>
            {{ t('aiAdmin.settings.title') }}
          </v-card-title>
          <v-card-text>
            <v-switch
              v-model="showReasoning"
              color="primary"
              inset
              hide-details
              :loading="aiSettingsSaving"
              :disabled="aiSettingsLoading"
              :label="t('aiAdmin.settings.showReasoning')"
              @update:model-value="saveAiSettings"
            />
            <div class="text-caption text-medium-emphasis mt-1">
              {{ t('aiAdmin.settings.showReasoningHint') }}
            </div>
            <v-alert v-if="aiSettingsError" type="error" variant="tonal" density="comfortable" class="mt-4">
              {{ aiSettingsError }}
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- Biblioteca delle azioni IA (cfg.json -> aiActions). Gli stati le richiamano per nome:
             la sequenza si configura qui una volta sola. -->
        <AiActionsEditor />

        <!-- Editor delle skill AI. Le azioni referenziano le skill per id; il testo si scrive qui. -->
        <SkillsEditor />
      </v-window-item>
    </v-window>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth.js'
import { apiCall } from '../utils/api.js'
import AiActionsEditor from '../components/AiActionsEditor.vue'
import SkillsEditor from '../components/SkillsEditor.vue'
import AiRunsLog from '../components/AiRunsLog.vue'
import { formatUsd, formatUsdShort } from '../utils/money.js'

// Gestione IA: consumi, credito e configurazione del motore. La rotta è già riservata ad admin e
// superadmin (requiresAdmin); dentro, saldo reale DeepSeek e configurazione restano solo superadmin.
const { t } = useI18n()
const authStore = useAuthStore()

const isSuperAdmin = computed(() => authStore.user?.role === 'superadmin')

const tab = ref('usage')

const formatDateTime = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  return isNaN(d.getTime()) ? iso : d.toLocaleString()
}

// Credito virtuale DeepSeek del cliente: GET /api/billing/deepseek-credit (ricarica − addebitato).
const creditLoading = ref(false)
const credit = ref(null)
const creditError = ref('')

const loadDeepSeekCredit = async () => {
  creditLoading.value = true
  creditError.value = ''
  try {
    credit.value = await apiCall('/api/billing/deepseek-credit', { method: 'GET' })
  } catch (e) {
    creditError.value = e?.message || t('admConfig.deepSeekCredit.error')
  } finally {
    creditLoading.value = false
  }
}

// Saldo reale del conto DeepSeek: GET /api/billing/deepseek-balance, solo superadmin.
const deepSeekLoading = ref(false)
const deepSeek = ref(null)
const deepSeekError = ref('')

const loadDeepSeekBalance = async () => {
  deepSeekLoading.value = true
  deepSeekError.value = ''
  try {
    deepSeek.value = await apiCall('/api/billing/deepseek-balance', { method: 'GET' })
  } catch (e) {
    deepSeekError.value = e?.message || t('admConfig.deepSeekBalance.error')
  } finally {
    deepSeekLoading.value = false
  }
}

// DeepSeek può riportare il saldo in USD o in CNY: la valuta arriva per riga.
const formatMoney = (amount, currency) => {
  if (amount === null || amount === undefined) return '—'
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(Number(amount))
  } catch {
    return `${Number(amount).toFixed(2)} ${currency || ''}`.trim()
  }
}

// Consumo IA misurato in casa (tabella AiUsage): dettaglio per modello e per processo.
const usage = ref(null)
const usageLoading = ref(false)
const usageError = ref('')
const usageFrom = ref('')
const usageTo = ref('')

// Colonna del costo reale del fornitore: il backend la valorizza solo per il superadmin, quindi
// compare solo quando c'e' davvero (usage.feePercent != null).
const providerColumn = computed(() => usage.value?.feePercent != null
  ? [{
      title: t('admConfig.usage.providerCost'), key: 'totals.providerCostUsd', align: 'end',
      value: (item) => formatUsd(item.totals.providerCostUsd),
    }]
  : [])

const usageModelHeaders = computed(() => [
  { title: t('projects.cost.engine'), key: 'engine' },
  { title: t('projects.cost.model'), key: 'model' },
  { title: t('projects.cost.runs'), key: 'totals.runs', align: 'end' },
  { title: t('projects.cost.failures'), key: 'totals.failedRuns', align: 'end' },
  {
    title: t('projects.cost.costColumn'), key: 'totals.costUsd', align: 'end',
    value: (item) => formatUsd(item.totals.costUsd),
  },
  ...providerColumn.value,
])

const usageProjectHeaders = computed(() => [
  { title: t('projects.cost.project'), key: 'idProject' },
  { title: t('projects.cost.runs'), key: 'totals.runs', align: 'end' },
  {
    title: t('projects.cost.costColumn'), key: 'totals.costUsd', align: 'end',
    value: (item) => formatUsd(item.totals.costUsd),
  },
  ...providerColumn.value,
  {
    title: t('admConfig.usage.lastRun'), key: 'lastRunAt',
    value: (item) => formatDateTime(item.lastRunAt),
  },
])

const loadUsage = async () => {
  usageLoading.value = true
  usageError.value = ''
  try {
    const qs = new URLSearchParams()
    if (usageFrom.value) qs.set('from', usageFrom.value)
    if (usageTo.value) qs.set('to', usageTo.value)
    const suffix = qs.toString() ? `?${qs}` : ''
    usage.value = await apiCall(`/api/billing/usage${suffix}`, { method: 'GET' })
  } catch (e) {
    usageError.value = e?.message || t('admConfig.usage.error')
  } finally {
    usageLoading.value = false
  }
}

// Impostazioni IA (solo superadmin): mostrare dal vivo il ragionamento del modello. Spento di
// default: il testo integrale di DeepSeek è troppo verboso. Il diario lo registra comunque.
const showReasoning = ref(false)
const aiSettingsLoading = ref(false)
const aiSettingsSaving = ref(false)
const aiSettingsError = ref('')

const loadAiSettings = async () => {
  aiSettingsLoading.value = true
  try {
    const data = await apiCall('/api/projects/configs/ai-settings', { method: 'GET' })
    showReasoning.value = !!data?.showReasoning
  } catch (e) {
    aiSettingsError.value = e?.message || t('aiAdmin.settings.error')
  } finally {
    aiSettingsLoading.value = false
  }
}

const saveAiSettings = async (value) => {
  aiSettingsSaving.value = true
  aiSettingsError.value = ''
  try {
    await apiCall('/api/projects/configs/ai-settings', {
      method: 'PUT',
      body: JSON.stringify({ showReasoning: !!value })
    })
  } catch (e) {
    showReasoning.value = !value
    aiSettingsError.value = e?.message || t('aiAdmin.settings.error')
  } finally {
    aiSettingsSaving.value = false
  }
}

onMounted(() => {
  loadUsage()
  loadDeepSeekCredit()
  if (isSuperAdmin.value) {
    loadDeepSeekBalance()
    loadAiSettings()
  }
})
</script>

<style scoped>
.v-card {
  margin-bottom: 16px;
}
</style>
